import { RefObject, useState } from 'react';
import { NavLink } from 'react-router-dom';
import ButtonPseudo from '../button/button-pseudo';
import {
  EnumNostroTabs,
  EnumTabs,
  EnumExchangeTabs,
  InternalEnumTabs,
} from '../transactions-sidepanels/types';
import IconUp from '../icons/up';
import IconDown from '../icons/down';

import './tabs.css';

interface Tab {
  name: string;
  value:
    | EnumTabs
    | EnumNostroTabs
    | EnumExchangeTabs
    | InternalEnumTabs
    | string;
  hidden?: boolean;
}

interface TabsProps {
  tabs:
    | Tab[]
    | {
        name: string | React.JSX.Element;
        path: string;
        hidden?: boolean;
      }[];
  hiddenRoutes?:
    | string[]
    | {
        name: string | React.JSX.Element;
        path: string;
        hidden?: boolean;
      }[];
  enableRouting?: boolean;
  onClick?: (
    tab:
      | EnumTabs
      | EnumNostroTabs
      | EnumExchangeTabs
      | InternalEnumTabs
      | string,
  ) => void;
  onRouterClick?: (e: React.MouseEvent) => void;
  activeTab?:
    | EnumTabs
    | EnumNostroTabs
    | EnumExchangeTabs
    | InternalEnumTabs
    | string;
  wrapperClassName?: string;
  itemClassName?: string;
  hideWarningTabs?: boolean;
  isTransactionsTabs?: boolean;
  withShadow?: boolean;
  withSticky?: boolean;
  warningsCount?: number;
  isExchangeTabs?: boolean;
  tabsClassName?: string;
  moreButtonClassName?: string;
  wrapperRef?: RefObject<HTMLDivElement | null>;
}

export default function Tabs({
  enableRouting,
  tabs,
  onClick,
  onRouterClick,
  activeTab,
  wrapperClassName = '',
  itemClassName = '',
  hideWarningTabs,
  isTransactionsTabs,
  withShadow = false,
  hiddenRoutes,
  withSticky = false,
  warningsCount,
  isExchangeTabs = false,
  tabsClassName = '',
  moreButtonClassName = '',
  wrapperRef,
}: TabsProps): React.JSX.Element {
  const [menuVisible, setMenuVisible] = useState(false);
  let content;
  let contentWrapper;

  let tabsArray = tabs;
  const hiddenTabs = tabs.slice(3);

  if (tabs.length > 3 && isTransactionsTabs) {
    tabsArray = tabs.slice(0, 3);
  }

  if (enableRouting) {
    content = (
      <>
        {(
          tabsArray as {
            name: string | React.JSX.Element;
            path: string;
            hidden?: boolean;
          }[]
        )
          ?.filter((item) => !item.hidden)
          .map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              className={`tab-item ${itemClassName} ${
                hideWarningTabs && tab.name === 'Warning' ? 'hidden-tab' : ''
              }`}
              onMouseEnter={() => setMenuVisible(false)}
              onClick={(e) => onRouterClick && onRouterClick(e)}
            >
              {tab.name === 'Warning'
                ? `${tab.name} ${
                    warningsCount && warningsCount > 0
                      ? `(${warningsCount})`
                      : ''
                  }`
                : tab.name}
            </NavLink>
          ))}
        {hiddenRoutes && hiddenRoutes.length > 0 && (
          <div className={`more-button-wrapper ${moreButtonClassName}`}>
            <button
              type="button"
              onMouseEnter={() => setMenuVisible(true)}
              className={`tab-item more-button ${menuVisible ? 'active' : ''} `}
              onClick={() => setMenuVisible(true)}
            >
              More
              {menuVisible ? <IconUp /> : <IconDown />}
            </button>
            {menuVisible && (
              <ul
                className="more-links"
                onMouseLeave={() => setMenuVisible(false)}
              >
                {(
                  hiddenRoutes as {
                    name: string | React.JSX.Element;
                    path: string;
                    hidden?: boolean;
                  }[]
                )
                  ?.filter((item) => !item.hidden)
                  .map((item) => (
                    <li
                      key={item.name as string}
                      className={
                        (hideWarningTabs && item.name === 'Warning') ||
                        (isExchangeTabs && item.name === 'Documents')
                          ? 'hidden-tab'
                          : ''
                      }
                    >
                      <NavLink
                        to={item.path}
                        onClick={(e) => {
                          if (onRouterClick) {
                            onRouterClick(e);
                          }
                          setMenuVisible(false);
                        }}
                        className="tab-link"
                      >
                        {item.name as string}
                      </NavLink>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
        {tabs.length > 3 &&
          isTransactionsTabs &&
          (
            hiddenTabs as {
              name: string | React.JSX.Element;
              path: string;
            }[]
          ).filter((item) => item.name !== 'Warning').length > 0 && (
            <div className={`more-button-wrapper ${moreButtonClassName}`}>
              <button
                type="button"
                onMouseEnter={() => setMenuVisible(true)}
                className={`tab-item more-button ${
                  menuVisible ? 'active' : ''
                }`}
                onClick={() => setMenuVisible(true)}
              >
                More
                {menuVisible ? <IconUp /> : <IconDown />}
              </button>
              {menuVisible && (
                <ul
                  className="more-links"
                  onMouseLeave={() => setMenuVisible(false)}
                >
                  {(
                    hiddenTabs as {
                      name: string | React.JSX.Element;
                      path: string;
                    }[]
                  ).map((item) => (
                    <li
                      key={item.name as string}
                      className={
                        (hideWarningTabs && item.name === 'Warning') ||
                        (isExchangeTabs && item.name === 'Documents')
                          ? 'hidden-tab'
                          : ''
                      }
                    >
                      <NavLink
                        to={item.path}
                        onClick={(e) => {
                          if (onRouterClick) {
                            onRouterClick(e);
                          }
                          setMenuVisible(false);
                        }}
                        className="tab-link"
                      >
                        {item.name as string}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
      </>
    );
  } else {
    let formattedHiddenTabs: Tab[] = hiddenTabs as Tab[];
    const formattedTabsArray: Tab[] = ([] as Tab[]).concat(tabsArray as Tab[]);

    if (hideWarningTabs) {
      const amlTab = (hiddenTabs as Tab[])?.find(
        (item: Tab) => item.name === 'AML & KYC',
      ) as Tab;

      formattedHiddenTabs = (hiddenTabs as Tab[]).filter(
        (item: Tab) => item.name !== 'AML & KYC',
      );

      if (amlTab) {
        formattedTabsArray.push(amlTab);
      }
    }

    content = (
      <>
        {formattedTabsArray
          ?.filter((tab) => !tab.hidden)
          .map((tab) => (
            <ButtonPseudo
              key={tab.name}
              text={
                tab.name === 'Warning'
                  ? `${tab.name} ${
                      warningsCount && warningsCount > 0
                        ? `(${warningsCount})`
                        : ''
                    }`
                  : tab.name
              }
              onClick={() => onClick && onClick(tab.value)}
              onMouseEnter={() => setMenuVisible(false)}
              className={`tab-item ${
                activeTab === tab.value ? 'active' : ''
              }  ${itemClassName} ${
                hideWarningTabs && tab.name === 'Warning' ? 'hidden-tab' : ''
              }`}
              bold
            />
          ))}
        {tabs?.length > 3 && isTransactionsTabs && (
          <div className={`more-button-wrapper ${moreButtonClassName}`}>
            <button
              type="button"
              onMouseEnter={() => setMenuVisible(true)}
              className={`tab-item more-button  ${menuVisible ? 'active' : ''}`}
              onClick={() => setMenuVisible(true)}
            >
              More
              {menuVisible ? <IconUp /> : <IconDown />}
            </button>
            {menuVisible && (
              <ul
                className="more-links"
                onMouseLeave={() => setMenuVisible(false)}
              >
                {formattedHiddenTabs
                  ?.filter((tab) => !tab.hidden)
                  ?.map((item) => (
                    <li
                      key={item.name}
                      className={
                        isExchangeTabs && item.name === 'Documents'
                          ? 'hide-documents-tab'
                          : ''
                      }
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (onClick) {
                            onClick(item.value);
                          }
                          setMenuVisible(false);
                        }}
                        className={activeTab === item.value ? 'active' : ''}
                      >
                        {item.name}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </>
    );
  }

  if (withSticky) {
    contentWrapper = (
      <div className={`tabs ${tabsClassName || ''}`}>
        <div className={`white-block ${withShadow ? 'with-shadow' : ''}`} />
        <div className={`tabs-wrapper ${wrapperClassName}`} ref={wrapperRef}>
          {content}
        </div>
      </div>
    );
  } else {
    contentWrapper = (
      <div className={`tabs-wrapper ${wrapperClassName}`} ref={wrapperRef}>
        {content}
      </div>
    );
  }

  return contentWrapper;
}
